import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, X } from "lucide-react";

// Shows an "Install app" button when the browser offers installation, and a
// "new version available" toast (safe auto-update) when a fresh build is ready.
export default function PwaManager() {
  const [installable, setInstallable] = useState(!!window.__deferredInstallPrompt);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("cs_install_dismissed") === "1");

  useEffect(() => {
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => setInstallable(false);
    const onUpdate = (e) => {
      const reg = e.detail;
      toast("A new version of CodexStudio is ready", {
        description: "Refresh to get the latest.",
        duration: Infinity,
        action: {
          label: "Refresh",
          onClick: () => {
            const w = reg?.waiting;
            if (w) w.postMessage("SKIP_WAITING"); // triggers controllerchange -> reload
            else window.location.reload();
          },
        },
      });
    };
    window.addEventListener("codex:pwa-installable", onInstallable);
    window.addEventListener("codex:pwa-installed", onInstalled);
    window.addEventListener("codex:sw-update", onUpdate);
    return () => {
      window.removeEventListener("codex:pwa-installable", onInstallable);
      window.removeEventListener("codex:pwa-installed", onInstalled);
      window.removeEventListener("codex:sw-update", onUpdate);
    };
  }, []);

  const install = async () => {
    const prompt = window.__deferredInstallPrompt;
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    window.__deferredInstallPrompt = null;
    setInstallable(false);
    if (outcome === "accepted") toast.success("CodexStudio installed");
  };

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("cs_install_dismissed", "1");
  };

  if (!installable || dismissed) return null;

  return (
    <div data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 z-[60] flex items-center gap-2 rounded-xl border border-primary/40 bg-popover/95 backdrop-blur px-3 py-2 shadow-lg fade-up">
      <button onClick={install} data-testid="pwa-install-button"
        className="flex items-center gap-2 text-sm font-semibold text-primary hover:opacity-90">
        <Download className="w-4 h-4" /> Install CodexStudio
      </button>
      <button onClick={dismiss} data-testid="pwa-install-dismiss" className="text-muted-foreground hover:text-foreground">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
